import { ServicePropertyType } from '@onlog-public/additional-services-types';
import { AdditionServicesOptionsState } from '@pages/AdditionalServices/containers/additionServicesOptionsContext';
import {
  AServiceCache,
  AServicePropertyCache,
  AServicePropertyFilterAndOrder,
  AdditionServicesState,
  FilterPropertyData,
  ListFilterData,
  NumberPropertyData,
  SwitchFilterData,
  VariantFilter,
} from '@pages/AdditionalServices/containers/types';

import { ServiceData, SetUpServiceData } from '../../../basket/types';
import { serviceRestoreStateGeneratorProcessor } from '../types';

/**
 * FilterWithAppliedFiltersProcessor валидирует применные фильтры установочной услуги,
 * задает сортировку и формирует новые данные на основе установочной услуги.
 */
export class FilterWithAppliedFiltersProcessor implements serviceRestoreStateGeneratorProcessor {
  /**
   * restore выполняет восстановление стейтов. Возвращает все стейты.
   * @param _
   * @param __
   * @param additionalServiceState
   * @param optionsState
   * @param serviceCacheState
   * @param servicePropertyCacheState
   * @param servicePropertyFilterAndOrderState
   */
  async restore(
    setUpServiceData: SetUpServiceData,
    __: (SetUpServiceData | ServiceData)[],
    additionalServiceState: AdditionServicesState,
    optionsState: AdditionServicesOptionsState,
    serviceCacheState: AServiceCache,
    servicePropertyCacheState: AServicePropertyCache,
    servicePropertyFilterAndOrderState: AServicePropertyFilterAndOrder
  ) {
    const appliedFilterData = setUpServiceData.FilterData;
    const newFilterData = { ...servicePropertyFilterAndOrderState };

    if (!appliedFilterData || Object.values(appliedFilterData.Variants).length === 0) {
      return {
        isValid: true,
        additionalServiceState,
        optionsState,
        serviceCacheState,
        servicePropertyCacheState,
        servicePropertyFilterAndOrderState: newFilterData,
      };
    }

    Object.keys(servicePropertyFilterAndOrderState.Variants).forEach((key) => {
      const appliedFilterToCurrentVariant = appliedFilterData.Variants[key] as VariantFilter;

      let currentMin = servicePropertyFilterAndOrderState.Variants[key].PriceFilter.CurrentMin;
      let currentMax = servicePropertyFilterAndOrderState.Variants[key].PriceFilter.CurrentMax;

      if (appliedFilterToCurrentVariant) {
        currentMin =
          servicePropertyFilterAndOrderState.Variants[key].PriceFilter.Max >=
          appliedFilterToCurrentVariant.PriceFilter.CurrentMin
            ? appliedFilterToCurrentVariant.PriceFilter.CurrentMin
            : servicePropertyFilterAndOrderState.Variants[key].PriceFilter.Min;

        currentMax =
          servicePropertyFilterAndOrderState.Variants[key].PriceFilter.Max <=
          appliedFilterToCurrentVariant.PriceFilter.CurrentMax
            ? servicePropertyFilterAndOrderState.Variants[key].PriceFilter.Max
            : appliedFilterToCurrentVariant.PriceFilter.CurrentMax;
      }

      newFilterData.Variants[key].PriceFilter = {
        ...servicePropertyFilterAndOrderState.Variants[key].PriceFilter,
        CurrentMin: currentMin,
        CurrentMax: currentMax,
      };

      let sortProperty = servicePropertyFilterAndOrderState.Variants[key].PropertyToSort;
      let sortDirection = servicePropertyFilterAndOrderState.Variants[key].SortDirection;
      let isSortByProperty = servicePropertyFilterAndOrderState.Variants[key].IsSortByProperty;

      if (appliedFilterToCurrentVariant.PropertyToSort === '') {
        const availablePriceDirection =
          additionalServiceState.LastServiceInPath.settings.TemplateSettings.ListTemplate
            .PriceAvailableDirection;

        sortProperty = appliedFilterToCurrentVariant.PropertyToSort;
        sortDirection =
          availablePriceDirection === 'both'
            ? appliedFilterToCurrentVariant.SortDirection
            : availablePriceDirection === 'asc'
            ? 'asc'
            : 'desc';
      }

      if (
        appliedFilterToCurrentVariant.IsSortByProperty &&
        appliedFilterToCurrentVariant.PropertyToSort !== ''
      ) {
        const foundProperty = additionalServiceState.PropertiesToDisplay.find(
          (prop) => prop.id === appliedFilterToCurrentVariant.PropertyToSort
        );

        if (foundProperty && foundProperty.settings.Sort.IsSortEnable) {
          const availableDirection = foundProperty.settings.Sort.AvailableDirection;

          sortProperty = appliedFilterToCurrentVariant.PropertyToSort;
          sortDirection =
            availableDirection === 'both'
              ? appliedFilterToCurrentVariant.SortDirection
              : availableDirection === 'asc'
              ? 'asc'
              : 'desc';
          isSortByProperty = true;
        }
      }

      newFilterData.Variants[key].PropertyToSort = sortProperty;
      newFilterData.Variants[key].SortDirection = sortDirection;
      newFilterData.Variants[key].IsSortByProperty = isSortByProperty;

      Object.keys(servicePropertyFilterAndOrderState.Variants[key].Data).forEach((dataKey) => {
        const appliedFilter = appliedFilterData.Variants[key].Data[dataKey];

        const filter = servicePropertyFilterAndOrderState.Variants[key].Data[
          dataKey
        ] as FilterPropertyData<ServicePropertyType>;

        if (!appliedFilter) {
          return;
        }

        if (filter.PropertyType === 'switch') {
          const switchFilter = filter as FilterPropertyData<'switch'>;
          const switchAppliedFilter = appliedFilter as FilterPropertyData<'switch'>;
          const current = switchAppliedFilter.FilterData.Current;

          if (switchFilter.FilterData.Type.includes(current)) {
            (newFilterData.Variants[key].Data[dataKey].FilterData as SwitchFilterData).Current =
              current;
          }
        }

        if (filter.PropertyType === 'number') {
          const numberFilter = filter as FilterPropertyData<'number'>;
          const numberAppliedFilter = appliedFilter as FilterPropertyData<'number'>;

          let currentMin = numberFilter.FilterData.Slider.CurrentMin;
          let currentMax = numberFilter.FilterData.Slider.CurrentMax;

          if (numberAppliedFilter) {
            currentMin =
              numberFilter.FilterData.Slider.Max >= numberAppliedFilter.FilterData.Slider.CurrentMin
                ? numberAppliedFilter.FilterData.Slider.CurrentMin
                : numberFilter.FilterData.Slider.Min;

            currentMax =
              numberFilter.FilterData.Slider.Max <= numberAppliedFilter.FilterData.Slider.CurrentMax
                ? numberFilter.FilterData.Slider.Max
                : numberAppliedFilter.FilterData.Slider.CurrentMax;
          }

          const numberFilterData = newFilterData.Variants[key].Data[dataKey]
            .FilterData as NumberPropertyData;

          numberFilterData.Slider = {
            ...numberFilter.FilterData.Slider,
            CurrentMin: currentMin,
            CurrentMax: currentMax,
          };

          const availabelSelectedOptionsIds = numberFilter.FilterData.List.Options.map(
            (option) => option.value
          );
          numberFilterData.List.Selected = numberAppliedFilter.FilterData.List.Selected.filter(
            (s) => availabelSelectedOptionsIds.includes(s)
          );
        }

        if (filter.PropertyType !== 'number' && filter.PropertyType !== 'switch') {
          const listFilterData = servicePropertyFilterAndOrderState.Variants[key].Data[dataKey]
            .FilterData as ListFilterData;
          const listAppliedFilter = appliedFilter.FilterData as ListFilterData;

          const availabelSelectedOptionsIds = listFilterData.Options.map((option) => option.value);

          const newListFilterData = newFilterData.Variants[key].Data[dataKey]
            .FilterData as ListFilterData;

          newListFilterData.Selected = listAppliedFilter.Selected.filter((s) =>
            availabelSelectedOptionsIds.includes(s)
          );
        }
      });
    });

    return {
      isValid: true,
      additionalServiceState,
      optionsState,
      serviceCacheState,
      servicePropertyCacheState,
      servicePropertyFilterAndOrderState: newFilterData,
    };
  }
}
